// src/pages/TreatmentsClinic/index.js
//
// Tedaviler — klinik geneli tedavi kayıtları. Düz liste (docs/design-principles.md §3):
// arama (hasta, TC, tanı) → durum çipleri → çerçevesiz tablo. Satıra tıklayınca
// sağdan drawer'da TreatmentEditor açılır; altta yetkiye bağlı "Hastaya git"
// hastanın Tedaviler sekmesine o tedavi seçili gider. Düzenleme "treatments:update".

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, LinearProgress, Table, TableBody, TableHead, TableRow, TableCell, Typography } from "@mui/material";
import { Healing as HealingIcon } from "@mui/icons-material";
import TreatmentEditor from "../../components/organisms/PatientWorkspaceTabs/TreatmentEditor";
import useAuth from "../../hooks/useAuth";
import useTerms from "../../hooks/useTerms";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useAlert } from "../../context/AlertProvider";
import { useClinicTheme } from "../../theme";
import { COLORS } from "../../styles/uiConstants";
import { useListClinicTreatmentsMutation } from "../../store/secretaryApi";
import { DataTableCard } from "../../components/organisms";
import {
  ClinicRecordListLayout,
  RecordEditorDrawer,
  ClickableRow,
  DateCell,
  Dash,
  EDGE_L,
  EDGE_R,
  FillerRow,
  PatientCell,
  PlainHead,
  ROWS_PER_PAGE_OPTIONS,
  SORTERS,
  SkeletonRows,
  SortHead,
  StatusPill,
  TextCell,
  cellSx,
  patientNameOf,
  mainText,
  subText,
  useClientList,
} from "../../components/organisms/ClinicRecordList";
import { fullDateTime } from "../Secretary/PatientsPage/patientListFormat";

const COLS = 6;
const MONTH_MS = 30 * 24 * 60 * 60 * 1000;

/** Treatment.status enum karşılıkları. */
const STATUS = {
  active: { label: "Aktif", tone: "success" },
  completed: { label: "Tamamlandı", tone: "neutral" },
  abandoned: { label: "Bırakıldı", tone: "warning" },
  cancelled: { label: "İptal", tone: "error" },
};

const SEGMENTS = [
  { value: "all", label: "Tümü" },
  { value: "active", label: "Aktif" },
  { value: "completed", label: "Tamamlandı" },
  { value: "abandoned", label: "Bırakıldı" },
  { value: "cancelled", label: "İptal" },
];

const segmentEmpty = (terms) => ({
  active: "Şu an süren tedavi yok.",
  completed: "Tamamlanmış tedavi yok.",
  abandoned: `${terms.patient} tarafından bırakılmış tedavi yok.`,
  cancelled: "İptal edilmiş tedavi yok.",
});

const statusOf = (t) => {
  const key = String(t?.status || "active").toLowerCase();
  return STATUS[key] ? key : "active";
};

const diagnosisNames = (t) => (t?.diagnoses || []).map((d) => d.diagnosisName).filter(Boolean);
const dateOf = (t) => t.createdInfo || t.createdAt;

const matches = (t, text) =>
  patientNameOf(t.user).toLocaleLowerCase("tr").includes(text) ||
  String(t.user?.tcNo || "").includes(text) ||
  diagnosisNames(t).some((n) => n.toLocaleLowerCase("tr").includes(text));

const SORT_FNS = { name: SORTERS.name((t) => t.user), date: SORTERS.date(dateOf) };
const DEFAULT_DIR = { name: "asc", date: "desc" };

/** Tedavinin kaçıncı ayında olduğu (hasta listesindeki "aktif tedavi" ile aynı hesap). */
const monthOf = (t) => {
  const duration = t.duration || 1;
  const elapsed = Math.max(0, Date.now() - new Date(dateOf(t)).getTime());
  return Math.min(duration, Math.max(1, Math.ceil(elapsed / MONTH_MS)));
};

export default function TreatmentsClinic() {
  const terms = useTerms();
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showError, showSuccess } = useAlert();
  const canEdit = useHasPermission("treatments:update");

  const [listClinicTreatments, { isLoading }] = useListClinicTreatmentsMutation();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const selected = useMemo(() => rows.find((r) => r._id === selectedId) || null, [rows, selectedId]);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await listClinicTreatments({ token }).unwrap();
      setRows(res?.data?.treatments || []);
    } catch (_) {
      showError("Tedaviler yüklenemedi.");
    } finally {
      setLoaded(true);
    }
  }, [token, listClinicTreatments, showError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const list = useClientList(rows, {
    segmentOf: statusOf,
    matches,
    sorters: SORT_FNS,
    defaultDir: DEFAULT_DIR,
    initialSort: "date",
  });

  const segments = useMemo(
    () => SEGMENTS.map((s) => ({ value: s.value, label: `${s.label} · ${list.counts[s.value] || 0}` })),
    [list.counts]
  );

  // Editör kaydı/aktiflik/durum değişince listeyi yerinde güncelle (hasta bilgisi korunur)
  const handleSaved = useCallback((updated) => {
    if (!updated?._id) return;
    setRows((prev) => prev.map((r) => (r._id === updated._id ? { ...r, ...updated, user: r.user } : r)));
  }, []);

  // Sunucuyla aynı: aynı hastanın diğer tedavilerinin isActive'i düşer
  const samePatient = (a, b) => String(a.user?._id || a.user) === String(b.user?._id || b.user);
  const handleSetActive = useCallback(
    (rec) => {
      setRows((prev) =>
        prev.map((r) =>
          r._id === rec._id
            ? { ...r, isActive: true, status: "active" }
            : samePatient(r, rec)
            ? { ...r, isActive: false }
            : r
        )
      );
      showSuccess("Aktif tedavi güncellendi.");
    },
    [showSuccess]
  );
  const handleSetStatus = useCallback(
    (rec, status) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r._id === rec._id) return { ...r, status, isActive: status === "active" };
          if (status === "active" && samePatient(r, rec)) return { ...r, isActive: false };
          return r;
        })
      );
      showSuccess("Tedavi durumu güncellendi.");
    },
    [showSuccess]
  );

  const handleEditorError = useCallback(
    (err) => {
      const status = err?.status || err?.originalStatus;
      const serverMsg = err?.data?.message;
      if (status === 423) {
        showError(serverMsg || `Bu ${terms.patientLower} şu an ${terms.examLoc} — değişiklik kaydedilemedi.`);
        return;
      }
      showError(serverMsg || "Tedavi kaydedilemedi.");
    },
    [showError, terms]
  );

  const busy = isLoading && !loaded;
  const empty = list.searchText
    ? { title: "Tedavi bulunamadı", message: `“${list.searchText}” aramanızla eşleşen tedavi yok. Aramayı değiştirin.` }
    : list.segment !== "all"
    ? { title: "Bu filtrede tedavi yok", message: segmentEmpty(terms)[list.segment] }
    : { title: "Henüz tedavi yok", message: `Tedaviler ${terms.patientLower} detayındaki Tedaviler sekmesinden oluşturulur.` };

  const selectedDiag = selected ? diagnosisNames(selected) : [];

  return (
    <>
    <ClinicRecordListLayout
      icon={<HealingIcon sx={{ fontSize: 22 }} />}
      title="Tedaviler"
      subtitle="Klinikteki tüm tedavi kayıtları ve ilerleyişi"
      search={list.search}
      onSearchChange={list.setSearch}
      searchPlaceholder={`${terms.patient} adı, TC no veya tanı ile ara`}
      segments={segments}
      segment={list.segment}
      onSegmentChange={list.setSegment}
      segmentAriaLabel="Tedavi durumu"
    >
      <DataTableCard
      loading={Boolean(busy)}
        variant="flat"
        empty={!busy && list.filtered.length === 0}
        emptyIcon={<HealingIcon />}
        emptyTitle={empty.title}
        emptyMessage={empty.message}
        page={list.page}
        rowsPerPage={list.rowsPerPage}
        totalCount={list.filtered.length}
        onPageChange={list.onPageChange}
        onRowsPerPageChange={list.onRowsPerPageChange}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      >
        <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              <SortHead field="name" label={terms.patient} {...sortProps(list)} sx={{ ...EDGE_L, width: "26%" }} />
              <PlainHead sx={{ width: "26%" }}>Tanı</PlainHead>
              <PlainHead sx={{ width: "16%" }}>İlerleme</PlainHead>
              <PlainHead sx={{ width: "12%" }}>Reçete / Diyet</PlainHead>
              <SortHead field="date" label="Başlangıç" defaultDir="desc" {...sortProps(list)} sx={{ width: "10%" }} />
              <PlainHead sx={{ ...EDGE_R, width: "10%" }}>Durum</PlainHead>
            </TableRow>
          </TableHead>
          <TableBody>
            {busy ? (
              <SkeletonRows rows={Math.min(10, list.rowsPerPage)} cols={COLS} />
            ) : list.pagedRows.length ? (
              list.pagedRows.map((t) => (
                <TreatmentRow
                  key={t._id}
                  row={t}
                  palette={palette}
                  onOpen={() => setSelectedId(t._id)}
                />
              ))
            ) : (
              <FillerRow cols={COLS} />
            )}
          </TableBody>
        </Table>
      </DataTableCard>
    </ClinicRecordListLayout>

    <RecordEditorDrawer
      open={Boolean(selected)}
      onClose={() => setSelectedId(null)}
      icon={<HealingIcon />}
      title={selected ? patientNameOf(selected.user) : "Tedavi"}
      subtitle={selected ? `${selectedDiag[0] || "Tedavi"} · ${fullDateTime(dateOf(selected))}` : ""}
      readOnly={!canEdit}
      patientLink={
        selected?.user?._id ? `/Hastalar/${selected.user._id}?tab=tedaviler&kayit=${selected._id}` : null
      }
    >
      {selected && (
        <TreatmentEditor
          key={selected._id}
          record={selected}
          mode="standalone"
          patient={selected.user}
          patientId={selected.user?._id}
          token={token}
          canEdit={canEdit}
          onSaved={handleSaved}
          onSetActive={handleSetActive}
          onSetStatus={handleSetStatus}
          onError={handleEditorError}
        />
      )}
    </RecordEditorDrawer>
    </>
  );
}

const sortProps = (list) => ({ sort: list.sort, dir: list.dir, onSort: list.onSort });

function TreatmentRow({ row, palette, onOpen }) {
  const key = statusOf(row);
  const names = diagnosisNames(row);
  const complaints = (row.complaints || []).filter(Boolean);
  const medCount = Array.isArray(row.medications) ? row.medications.length : 0;
  const duration = row.duration || 1;
  const month = monthOf(row);

  const rxParts = [medCount ? `${medCount} ilaç` : null, row.diet ? "Diyet" : null].filter(Boolean);
  const requests = [row.prescriptionRequested && !medCount ? "Reçete" : null, row.dietRequested && !row.diet ? "Diyet" : null].filter(Boolean);

  return (
    <ClickableRow onClick={onOpen} palette={palette}>
      <PatientCell patient={row.user} />

      <TextCell
        main={names[0] || complaints[0]}
        sub={
          names.length > 1
            ? `+${names.length - 1} tanı daha`
            : !names.length && complaints.length
            ? "Şikayet"
            : null
        }
        tooltip={names.length > 1 ? names.join(", ") : null}
      />

      {/* İlerleme: aktifte ay çubuğu, diğerlerinde yalnız süre */}
      <TableCell sx={cellSx}>
        {key === "active" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 2 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (month / duration) * 100)}
              sx={{
                flex: 1,
                maxWidth: 90,
                height: 4,
                borderRadius: 2,
                bgcolor: COLORS.slate100,
                "& .MuiLinearProgress-bar": { bgcolor: palette.primary, borderRadius: 2 },
              }}
            />
            <Typography sx={{ ...subText, whiteSpace: "nowrap" }}>
              {month}/{duration}. ay
            </Typography>
          </Box>
        ) : (
          <Typography sx={subText}>{duration} aylık</Typography>
        )}
      </TableCell>

      <TableCell sx={cellSx}>
        {rxParts.length || requests.length ? (
          <Box sx={{ minWidth: 0 }}>
            {rxParts.length ? (
              <Typography sx={mainText} noWrap>
                {rxParts.join(" · ")}
              </Typography>
            ) : null}
            {requests.length ? (
              <Typography sx={{ ...subText, color: COLORS.warningDark }} noWrap>
                {requests.join(", ")} talebi
              </Typography>
            ) : null}
          </Box>
        ) : (
          <Dash />
        )}
      </TableCell>

      <DateCell value={dateOf(row)} />

      <TableCell sx={{ ...cellSx, ...EDGE_R }}>
        <StatusPill label={STATUS[key].label} tone={STATUS[key].tone} palette={palette} />
      </TableCell>
    </ClickableRow>
  );
}
